/**
 * Pure text-manipulation helpers for an AsciiDoc markup editor — the
 * AsciiDoc counterpart to `@/lib/djotEditing`. Same design: every function
 * takes an `EditorSelection` (full text + selection range) and returns a
 * new one, with no DOM coupling.
 *
 * AsciiDoc inline delimiters (unlike Djot, which swaps `*`/`_`) match
 * intuition: `*strong*` is bold, `_emphasis_` is italic, `` `mono` `` is
 * monospace, `^sup^` / `~sub~` are super/subscript, `#highlight#` marks
 * highlighted text.
 */
import type { EditorSelection } from '@/lib/djotEditing';

export type { EditorSelection };

function getLineBounds(text: string, index: number): [number, number] {
  const start = text.lastIndexOf('\n', index - 1) + 1;
  let end = text.indexOf('\n', index);
  if (end === -1) end = text.length;
  return [start, end];
}

function getSelectedLineBounds(text: string, start: number, end: number): [number, number] {
  const [lineStart] = getLineBounds(text, start);
  const adjustedEnd = end > start && text[end - 1] === '\n' ? end - 1 : end;
  const [, lineEnd] = getLineBounds(text, adjustedEnd);
  return [lineStart, lineEnd];
}

/** Wraps (or unwraps) the selection with inline delimiters, e.g. `*` for strong. */
export function toggleInlineWrap(
  sel: EditorSelection,
  delimiter: string,
  placeholder: string,
): EditorSelection {
  const { value, start, end } = sel;
  const selected = value.slice(start, end);
  const dLen = delimiter.length;

  if (selected.length >= dLen * 2 && selected.startsWith(delimiter) && selected.endsWith(delimiter)) {
    const inner = selected.slice(dLen, selected.length - dLen);
    const newValue = value.slice(0, start) + inner + value.slice(end);
    return { value: newValue, start, end: start + inner.length };
  }

  const beforeSlice = value.slice(Math.max(0, start - dLen), start);
  const afterSlice = value.slice(end, end + dLen);
  if (!selected && beforeSlice === delimiter && afterSlice === delimiter) {
    const newValue = value.slice(0, start - dLen) + value.slice(start, end) + value.slice(end + dLen);
    const pos = start - dLen;
    return { value: newValue, start: pos, end: pos };
  }

  const content = selected || placeholder;
  const newValue = value.slice(0, start) + delimiter + content + delimiter + value.slice(end);
  return { value: newValue, start: start + dLen, end: start + dLen + content.length };
}

/** Toggles a literal per-line prefix (e.g. a bullet marker) across every line touched by the selection. */
export function toggleLinePrefix(sel: EditorSelection, prefix: string): EditorSelection {
  const { value, start, end } = sel;
  const [lineStart, lineEnd] = getSelectedLineBounds(value, start, end);
  const block = value.slice(lineStart, lineEnd);
  const lines = block.split('\n');
  const nonBlank = lines.filter((l) => l.trim() !== '');
  const allPrefixed = nonBlank.length > 0 && nonBlank.every((l) => l.startsWith(prefix));

  const newLines = lines.map((l) => {
    if (l.trim() === '') return l;
    return allPrefixed ? l.slice(prefix.length) : prefix + l;
  });

  const newBlock = newLines.join('\n');
  const newValue = value.slice(0, lineStart) + newBlock + value.slice(lineEnd);
  const delta = newBlock.length - block.length;
  return { value: newValue, start: lineStart, end: Math.max(lineStart, lineEnd + delta) };
}

/** Toggles a numbered ordered-list prefix (AsciiDoc numbers are conventionally always `1.`). */
export function toggleOrderedList(sel: EditorSelection): EditorSelection {
  return toggleLinePrefix(sel, '. ');
}

/** Sets (or clears, if already at that level) an AsciiDoc heading marker (`=` .. `======`) on the cursor's line. */
export function setHeadingLevel(sel: EditorSelection, level: number): EditorSelection {
  const { value, start, end } = sel;
  const [lineStart, lineEnd] = getLineBounds(value, start);
  const line = value.slice(lineStart, lineEnd);
  const match = line.match(/^(={1,6})\s*/);
  const currentLevel = match ? match[1].length : 0;
  const stripped = match ? line.slice(match[0].length) : line;
  const newLine = currentLevel === level ? stripped : `${'='.repeat(level)} ${stripped}`;
  const newValue = value.slice(0, lineStart) + newLine + value.slice(lineEnd);
  const delta = newLine.length - line.length;
  return {
    value: newValue,
    start: Math.max(lineStart, start + delta),
    end: Math.max(lineStart, end + delta),
  };
}

/** Inserts a `link:url[text]` (or `image:url[alt]`), selecting the URL placeholder. */
export function insertLink(sel: EditorSelection, isImage: boolean): EditorSelection {
  const { value, start, end } = sel;
  const selected = value.slice(start, end) || (isImage ? 'alt text' : 'link text');
  const macro = isImage ? 'image' : 'link';
  const inserted = `${macro}:url[${selected}]`;
  const newValue = value.slice(0, start) + inserted + value.slice(end);
  const urlStart = start + macro.length + 1;
  return { value: newValue, start: urlStart, end: urlStart + 3 };
}

/** Inserts a wikilink (`[[Article Name]]`), selecting the article-name placeholder. Native NKBIP-01 syntax — no expansion needed at publish time. */
export function insertWikilink(sel: EditorSelection): EditorSelection {
  const { value, start, end } = sel;
  const selected = value.slice(start, end) || 'Article name';
  const inserted = `[[${selected}]]`;
  return { value: value.slice(0, start) + inserted + value.slice(end), start: start + 2, end: start + 2 + selected.length };
}

/** Inserts a self-contained delimited block at the cursor, with blank-line separation from surrounding text. */
export function insertBlockAtCursor(
  sel: EditorSelection,
  buildTemplate: (selectedText: string) => string,
): EditorSelection {
  const { value, start, end } = sel;
  const selected = value.slice(start, end);
  const template = buildTemplate(selected);

  const before = value.slice(0, start);
  const leadingNewlines = before.length === 0 ? '' : /\n\n$/.test(before) ? '' : /\n$/.test(before) ? '\n' : '\n\n';

  const after = value.slice(end);
  const trailingNewlines = after.length === 0 ? '' : /^\n\n/.test(after) ? '' : /^\n/.test(after) ? '\n' : '\n\n';

  const inserted = leadingNewlines + template + trailingNewlines;
  const newValue = before + inserted + after;
  const blockStart = start + leadingNewlines.length;
  return { value: newValue, start: blockStart, end: blockStart + template.length };
}

export function codeBlockTemplate(selected: string): string {
  return `[source]\n----\n${selected || 'code here'}\n----`;
}

export function admonitionTemplate(kind: 'NOTE' | 'TIP' | 'IMPORTANT' | 'WARNING' | 'CAUTION') {
  return (selected: string) => `${kind}: ${selected || 'Callout text.'}`;
}

export function tableTemplate(): string {
  return ['|===', '|Column 1 |Column 2', '', '|Cell', '|Cell', '|==='].join('\n');
}

export function thematicBreakTemplate(): string {
  return "'''";
}

export function quoteTemplate(selected: string): string {
  return `[quote]\n____\n${selected || 'Quoted text.'}\n____`;
}
